import type { LitElement, ReactiveController, ReactiveControllerHost } from "lit";

import type { Ref } from "lit/directives/ref.js";
import type { ItemCard } from "./item-card";

/**
 * Options for configuring the DnD item controller
 */
export interface DnDItemOptions {
  dragHandle?: Ref<Element>;
}

/**
 * Generic drag and drop item controller that makes any item draggable
 * Works with any data type and integrates with DnDContainerController
 * Uses pointer events for immediate response on mobile devices
 */
export class DnDItemController<T> implements ReactiveController {
  private itemElement: HTMLElement | null = null;
  private isDragging = false;
  private abortController = new AbortController();
  private dragGhost: HTMLElement | null = null;
  private startPointer: { x: number; y: number } | null = null;
  private currentPointer: { x: number; y: number } | null = null;
  private dragStarted = false;
  private dragThreshold = 5; // pixels to move before starting drag
  private autoScrollAnimationId: number | null = null;
  private scrollableContainer: HTMLElement | null = null;
  private readonly autoScrollEdgeThreshold = 50;
  private readonly autoScrollMaxSpeed = 15;

  constructor(
    private host: ReactiveControllerHost,
    private itemData: T,
    private options: DnDItemOptions,
  ) {
    host.addController(this);
  }

  hostConnected() {
    this.setupEventListeners();
  }

  hostDisconnected() {
    this.cleanup();
  }

  offsetY = 70;
  offsetX = 25;

  hostUpdated() {
    const hostElement = this.host as unknown as HTMLElement & { shadowRoot?: ShadowRoot };

    this.itemElement = hostElement;

    this.offsetY = hostElement.getAttribute("type") === "square" ? 70 : 35;

    this.setupEventListeners();
  }

  private setupEventListeners() {
    if (!this.itemElement) return;

    // Clean up existing listeners first
    this.cleanup();

    // Create new abort controller for this setup
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // Get drag handle element or use the whole item
    const dragHandle = this.options.dragHandle?.value || this.itemElement;

    // Set up pointer event listeners on the handle for dragging
    if (dragHandle) {
      dragHandle.addEventListener("pointerdown", this.handlePointerDown as EventListener, { signal });
    }

    // Set up pointer event listeners on the item element for drop zone detection
    this.itemElement.addEventListener("pointerover", this.handlePointerOver as EventListener, { signal });
    this.itemElement.addEventListener("pointerleave", this.handlePointerLeave as EventListener, { signal });

    // Global pointer events for dragging
    document.addEventListener("pointermove", this.handlePointerMove as EventListener, { signal });
    document.addEventListener("pointerup", this.handlePointerUp as EventListener, { signal });
    document.addEventListener("pointercancel", this.handlePointerCancel as EventListener, { signal });
  }

  private handlePointerDown = (event: PointerEvent) => {
    // Only allow dragging when clicking the drag handle
    const dragHandle = this.options.dragHandle?.value;
    if (dragHandle && !dragHandle.contains(event.target as Node)) {
      return;
    }

    // Only prevent default if we’re about to drag
    // (but not on simple taps/clicks)
    this.startPointer = { x: event.clientX, y: event.clientY };
    this.currentPointer = { x: event.clientX, y: event.clientY };
    this.dragStarted = false;

    // Don’t prevent default here — let click events work
    // We'll prevent default later when drag actually starts.
  };

  private handlePointerMove = (event: PointerEvent) => {
    if (!this.startPointer) {
      return;
    }

    this.currentPointer = { x: event.clientX, y: event.clientY };

    // Calculate distance moved
    const dx = this.currentPointer.x - this.startPointer.x;
    const dy = this.currentPointer.y - this.startPointer.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Start dragging if we've moved past the threshold
    if (!this.dragStarted && distance > this.dragThreshold) {
      try {
        this.startDrag();
        event.preventDefault();
      } catch (error) {
        console.error("Error in startDrag():", error);
        this.endDrag(); // Clean up on error
      }
    }

    // Update drag ghost position
    if (this.isDragging && this.dragGhost) {
      this.updateDragGhost(this.currentPointer.x, this.currentPointer.y);
    }

    // Handle drop zone detection
    if (this.isDragging) {
      this.handleDropZoneDetection(this.currentPointer.x, this.currentPointer.y);
      this.checkAutoScroll(this.currentPointer.x, this.currentPointer.y);
    }

    event.preventDefault();
  };

  private handlePointerUp = (event: PointerEvent) => {
    if (this.isDragging) {
      this.handleDrop(this.currentPointer?.x || 0, this.currentPointer?.y || 0);
    }

    this.endDrag();
    event.preventDefault();
  };

  private handlePointerCancel = () => {
    this.endDrag();
  };

  private startDrag() {
    this.dragStarted = true;
    this.isDragging = true;

    this.scrollableContainer = this.findScrollableContainer();
    this.createDragGhost();
    this.host.requestUpdate();
    // Emit drag start event
    this.emitDragEvent("item-drag-start", { item: this.itemData });
  }

  private endDrag() {
    const wasDragging = this.isDragging;

    this.isDragging = false;
    this.dragStarted = false;
    this.startPointer = null;
    this.currentPointer = null;
    this.stopAutoScroll();
    this.scrollableContainer = null;
    this.removeDragGhost();
    this.host.requestUpdate();

    // Emit drag end event
    if (wasDragging) {
      this.emitDragEvent("item-drag-end", { item: this.itemData });
    }
  }

  private createDragGhost() {
    if (!this.itemElement) return;

    const clone = this.itemElement.cloneNode(true) as ItemCard;
    clone.item = (this.itemElement as ItemCard).item;

    const rect = (this.host as LitElement).renderRoot.firstElementChild?.getBoundingClientRect();

    clone.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 10000;
      opacity: 0.8;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      transition: none;
      transform: scale(0.85);
      overflow: hidden;
      top: ${(this.currentPointer?.y ?? this.offsetY) - this.offsetY}px;
      left: ${(this.currentPointer?.x ?? this.offsetX) - this.offsetX}px;
      max-width: ${rect?.width}px;
    `;

    this.dragGhost = clone;

    document.body.appendChild(this.dragGhost);

    if (this.currentPointer) {
      this.updateDragGhost(this.currentPointer.x, this.currentPointer.y);
    }
  }

  private updateDragGhost(x: number, y: number) {
    if (!this.dragGhost || !this.itemElement) return;

    this.dragGhost.style.left = `${x - this.offsetX}px`;
    this.dragGhost.style.top = `${y - this.offsetY}px`;
  }

  private removeDragGhost() {
    if (this.dragGhost) {
      document.body.removeChild(this.dragGhost);
      this.dragGhost = null;
    }
  }

  private handleDropZoneDetection(x: number, y: number) {
    // Find the element under the pointer (excluding the drag ghost)
    if (this.dragGhost) {
      this.dragGhost.style.display = "none";
    }

    // Try multiple points around the current pointer to expand detection area
    const detectionRadius = 40; // pixels
    const testPoints = [
      { x, y }, // Center point
      { x: x - detectionRadius, y }, // Left
      { x: x + detectionRadius, y }, // Right
      { x, y: y - detectionRadius }, // Top
      { x, y: y + detectionRadius }, // Bottom
      { x: x - detectionRadius, y: y - detectionRadius }, // Top-left
      { x: x + detectionRadius, y: y - detectionRadius }, // Top-right
      { x: x - detectionRadius, y: y + detectionRadius }, // Bottom-left
      { x: x + detectionRadius, y: y + detectionRadius }, // Bottom-right
    ];

    let closestItem = null;
    let closestDistance = Infinity;

    for (const point of testPoints) {
      const elementUnder = this.elementFromPointDeep(point.x, point.y);
      const itemCard = this.findDraggableItemInShadowDOM(elementUnder);

      if (itemCard && itemCard !== this.itemElement) {
        // Calculate distance from original pointer to this item's center
        const rect = itemCard.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = { element: itemCard, rect };
        }
      }
    }

    if (this.dragGhost) {
      this.dragGhost.style.display = "block";
    }

    if (closestItem) {
      this.calculateDropPosition(closestItem.element, closestItem.rect, x, y);
    }
  }

  private calculateDropPosition(targetElement: HTMLElement, rect: DOMRect, x: number, y: number) {
    // Request valid drop positions from the container
    let validPositions: ("top" | "right" | "bottom" | "left")[] = [];
    this.requestValidDropPositions((positions) => {
      validPositions = positions;
    });

    if (validPositions.length === 0) return;

    // Calculate the closest valid drop side based on pointer position
    let side: "left" | "right" | "top" | "bottom";

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const isLeft = x < centerX;
    const isTop = y < centerY;

    // Find the best valid position based on pointer location
    if (validPositions.includes("top") && isTop) {
      side = "top";
    } else if (validPositions.includes("bottom") && !isTop) {
      side = "bottom";
    } else if (validPositions.includes("left") && isLeft) {
      side = "left";
    } else if (validPositions.includes("right") && !isLeft) {
      side = "right";
    } else {
      // Default to first valid position
      side = validPositions[0] || "top";
    }

    // Get the item id from the target element
    const itemId = this.getItemIdFromElement(targetElement);

    // Emit drag over event with item id and position
    this.emitDragEvent("item-drag-over", {
      itemId,
      side,
      targetElement,
    });
  }

  private handleDrop(x: number, y: number) {
    // Find the element under the pointer for final drop
    if (this.dragGhost) {
      this.dragGhost.style.display = "none";
    }

    const elementUnder = document.elementFromPoint(x, y);

    if (this.dragGhost) {
      this.dragGhost.style.display = "block";
    }

    // Emit custom drop event to container
    const dropEvent = new CustomEvent("custom-drop", {
      bubbles: true,
      composed: true,
      detail: {
        x,
        y,
        elementUnder,
        item: this.itemData,
      },
    });

    if (this.itemElement) {
      this.itemElement.dispatchEvent(dropEvent);
    }
  }

  private handlePointerOver = (event: PointerEvent) => {
    // Only handle if we're dragging and this isn't the dragged item
    if (!this.isDragging || event.target === this.itemElement) return;

    event.preventDefault();
    event.stopPropagation();
  };

  private handlePointerLeave = (event: PointerEvent) => {
    if (!this.isDragging) return;

    // Only emit leave if we're actually leaving the item bounds
    if (!this.itemElement) return;

    const rect = this.itemElement.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      this.emitDragEvent("item-drag-leave", { itemId: this.getItemId() });
    }
  };

  private getItemId(): string {
    // Extract id from item data
    if (this.itemData && typeof this.itemData === "object" && "id" in this.itemData) {
      return String((this.itemData as Record<string, unknown>).id);
    }
    return "";
  }

  private getItemIdFromElement(element: HTMLElement): string {
    // Walk up the DOM tree to find an element with data-item-id
    let current: HTMLElement | null = element;
    let depth = 0;
    const maxDepth = 10;

    while (current && depth < maxDepth) {
      // Check if the element has data-item-id attribute
      if (current.dataset && current.dataset.itemId) {
        return current.dataset.itemId;
      }

      current = current.parentElement;
      depth++;
    }

    return "";
  }

  private requestValidDropPositions(callback: (positions: ("top" | "right" | "bottom" | "left")[]) => void) {
    // Request valid drop positions from container
    const event = new CustomEvent("get-valid-drop-positions", {
      bubbles: true,
      composed: true,
      detail: { callback },
    });

    if (this.itemElement) {
      this.itemElement.dispatchEvent(event);
    }
  }

  private elementFromPointDeep(x: number, y: number): Element | null {
    // Start with the top-level element
    let element = document.elementFromPoint(x, y);
    // Traverse into shadow DOMs
    let depth = 0;
    const maxDepth = 10;

    while (element && depth < maxDepth) {
      depth++;

      // Check if this element has a shadow root
      const elementWithShadow = element as HTMLElement & { shadowRoot?: ShadowRoot };
      if (elementWithShadow.shadowRoot) {
        const shadowElement = elementWithShadow.shadowRoot.elementFromPoint(x, y);
        if (shadowElement && shadowElement !== element) {
          element = shadowElement;
          continue; // Keep looking for deeper shadow DOMs
        }
      }

      break; // No more shadow DOMs to traverse
    }
    return element;
  }

  private findDraggableItemInShadowDOM(element: Element | null): HTMLElement | null {
    if (!element) {
      return null;
    }

    let current: Element | null = element;
    let depth = 0;
    const maxDepth = 20; // Prevent infinite loops

    while (current && depth < maxDepth) {
      depth++;

      // Check if current element has draggable-item class
      if (current.classList?.contains("draggable-item")) {
        return current as HTMLElement;
      }

      // Check if we're inside an item-card
      if (current.tagName?.toLowerCase() === "item-card") {
        // Look for .draggable-item inside the item-card's shadow root
        const itemCard = current as HTMLElement & { shadowRoot?: ShadowRoot };
        if (itemCard.shadowRoot) {
          const draggableItem = itemCard.shadowRoot.querySelector(".draggable-item") as HTMLElement;
          if (draggableItem) {
            return draggableItem;
          }
        }
      }

      // Try closest() method which works across shadow boundaries in modern browsers
      try {
        const closest = current.closest?.(".draggable-item");
        if (closest) {
          return closest as HTMLElement;
        }
      } catch {
        // Ignore closest() errors
      }

      // Move to parent element or cross shadow boundary
      if (current.parentElement) {
        current = current.parentElement;
      } else {
        // Try to cross shadow boundary
        const root = current.getRootNode();
        if (root instanceof ShadowRoot && root.host) {
          current = root.host;
        } else {
          break;
        }
      }
    }

    return null;
  }

  private emitDragEvent(eventName: string, detail: Record<string, unknown>) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
      detail,
    });

    if (this.itemElement) {
      this.itemElement.dispatchEvent(event);
    }
  }

  private findScrollableContainer(): HTMLElement | null {
    if (!this.itemElement) return null;

    let current: HTMLElement | null = this.itemElement;
    let depth = 0;
    const maxDepth = 20;

    while (current && depth < maxDepth) {
      depth++;

      // Check if this element has the scrollable-column-content class
      if (current.classList?.contains("scrollable-column-content")) {
        return current;
      }

      // Move to parent or cross shadow boundary
      if (current.parentElement) {
        current = current.parentElement;
      } else {
        const root = current.getRootNode();
        if (root instanceof ShadowRoot && root.host) {
          current = root.host as HTMLElement;
        } else {
          break;
        }
      }
    }

    return null;
  }

  private checkAutoScroll(x: number, y: number) {
    if (!this.scrollableContainer) return;

    const rect = this.scrollableContainer.getBoundingClientRect();
    const threshold = this.autoScrollEdgeThreshold;

    // Calculate distances from edges
    const distanceFromTop = y - rect.top;
    const distanceFromBottom = rect.bottom - y;

    let scrollSpeed = 0;

    // Check if near top edge
    if (distanceFromTop < threshold && distanceFromTop > 0) {
      scrollSpeed = -this.calculateScrollSpeed(distanceFromTop, threshold);
    }
    // Check if near bottom edge
    else if (distanceFromBottom < threshold && distanceFromBottom > 0) {
      scrollSpeed = this.calculateScrollSpeed(distanceFromBottom, threshold);
    }

    if (scrollSpeed !== 0) {
      this.startAutoScroll(scrollSpeed);
    } else {
      this.stopAutoScroll();
    }
  }

  private calculateScrollSpeed(distance: number, threshold: number): number {
    // Speed increases as we get closer to the edge
    const ratio = 1 - distance / threshold;
    return ratio * this.autoScrollMaxSpeed;
  }

  private startAutoScroll(speed: number) {
    if (this.autoScrollAnimationId !== null) {
      return; // Already scrolling
    }

    const scroll = () => {
      if (!this.scrollableContainer || !this.isDragging) {
        this.stopAutoScroll();
        return;
      }

      this.scrollableContainer.scrollTop += speed;
      this.autoScrollAnimationId = requestAnimationFrame(scroll);
    };

    this.autoScrollAnimationId = requestAnimationFrame(scroll);
  }

  private stopAutoScroll() {
    if (this.autoScrollAnimationId !== null) {
      cancelAnimationFrame(this.autoScrollAnimationId);
      this.autoScrollAnimationId = null;
    }
  }

  private cleanup() {
    this.abortController.abort();
    this.stopAutoScroll();

    // Only clear drag state if we're not currently dragging
    if (!this.isDragging) {
      this.removeDragGhost();
      this.startPointer = null;
      this.currentPointer = null;
      this.dragStarted = false;
      this.scrollableContainer = null;
    }
  }

  // Public getters for template usage
  get isCurrentlyDragging() {
    return this.isDragging;
  }

  // Method to update item data
  updateItemData(newData: T) {
    this.itemData = newData;
  }
}
